"use client";

/**
 * /why-bb/story: the Why BB story plus one film per kind of member (Gordon
 * 2026-11-03: "more films on Story that show all the tools and features, for
 * the different user accounts … how they can use BB"). Pick a role under the
 * player; `?film=<role>` opens one directly (shareable). Signed-in members see
 * the films that fit their own roles first, marked "Your role".
 */

import { useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import {
  AudioLines, Briefcase, Building2, Camera, Disc3, Guitar, Library, MicVocal, Palette, PenLine,
  Play, Podcast, Route, Scissors, SlidersHorizontal, Sparkles, Video, Zap, type LucideIcon,
} from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { ROLE_FILMS, ROLE_KEYS, roleFilmsFor, type Film, type RoleKey } from "@/components/screens/films";
import { FilmStage } from "@/components/screens/film-stage";
import { useBbArt, useFilmLines } from "@/components/screens/film-art";
import { useBeatClock } from "@/components/screens/use-beat-clock";
import { MOCK_CSS } from "@/components/screens/mocks";
import { cn } from "@/lib/utils";
import { StoryFrame } from "./story-frame";
import { WhyStory } from "./why-story";
import { PulseFilm, TourFilm } from "./tour-stage";

/** "tour" and "pulse" walk through the whole app (tour-films.ts). */
type Pick = "why" | "tour" | "pulse" | RoleKey;
const APP_FILMS = ["tour", "pulse"] as const;

const ICONS: Record<Pick, LucideIcon> = {
  why: Sparkles,
  tour: Route,
  pulse: Zap,
  artist: MicVocal,
  producer: SlidersHorizontal,
  musician: Guitar,
  songwriter: PenLine,
  engineer: AudioLines,
  dj: Disc3,
  podcaster: Podcast,
  director: Video,
  editor: Scissors,
  photographer: Camera,
  designer: Palette,
  label: Library,
  manager: Briefcase,
  venue: Building2,
};

function RoleFilm({ film, name, covers, people }: { film: Film; name: string; covers: string[]; people: string[] }) {
  const linesFor = useFilmLines();
  const [playing, setPlaying] = useState(true);
  const lengths = useMemo(() => film.beats.map(b => b.ms), [film]);
  const clock = useBeatClock(lengths, { playing, loop: true });
  return (
    <StoryFrame
      label={name}
      playing={playing}
      onTogglePlay={() => setPlaying(p => !p)}
      onPrev={() => clock.goTo(clock.beat - 1)}
      onNext={() => clock.goTo(clock.beat + 1)}
      onRestart={() => { clock.goTo(0); setPlaying(true); }}
    >
      <style>{MOCK_CSS}</style>
      <FilmStage film={film} beat={clock.beat} progress={clock.progress} round={clock.round} covers={covers} people={people} linesFor={linesFor} />
    </StoryFrame>
  );
}

export function StoryFilms() {
  const t = useTranslations("roleFilms");
  const tStory = useTranslations("whyStory");
  const tTour = useTranslations("tourFilms");
  const { userProfile } = useAuth();
  const { covers, people } = useBbArt();
  const [pick, setPick] = useState<Pick>("why");
  const playerRef = useRef<HTMLDivElement>(null);

  // ?film=<role> opens that film (read once; no Suspense needed).
  useEffect(() => {
    const f = new URLSearchParams(window.location.search).get("film");
    if (f && ([...APP_FILMS, ...ROLE_KEYS] as readonly string[]).includes(f)) setPick(f as Pick);
  }, []);

  const mine = useMemo(() => roleFilmsFor(userProfile?.roles || [], userProfile?.accountType), [userProfile?.roles, userProfile?.accountType]);
  const order: Pick[] = useMemo(() => ["why", ...APP_FILMS, ...mine, ...ROLE_KEYS.filter(k => !mine.includes(k))], [mine]);
  const nameOf = (key: Pick) => (key === "why" ? t("why") : key === "tour" || key === "pulse" ? tTour(`${key}.name`) : t(`${key}.name`));

  const choose = (key: Pick) => {
    setPick(key);
    const url = new URL(window.location.href);
    if (key === "why") url.searchParams.delete("film");
    else url.searchParams.set("film", key);
    window.history.replaceState(window.history.state, "", url.toString());
    // The player sits above the tiles; on a phone it has scrolled away.
    const top = playerRef.current?.getBoundingClientRect().top ?? 0;
    if (top < 0 || top > window.innerHeight * 0.5) playerRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const film = pick === "why" || pick === "tour" || pick === "pulse" ? null : ROLE_FILMS.find(f => f.id === `role-${pick}`) ?? null;

  return (
    <div className="space-y-6">
      <div className="space-y-3">
        {/* Fits the screen under the header and above the player. */}
        <div ref={playerRef} className="max-w-[calc((100dvh_-_var(--header-height)_-_10rem)*16/9)] scroll-mt-[calc(var(--header-height)_+_12px)]">
          {pick === "tour" ? <TourFilm name={nameOf(pick)} covers={covers} people={people} />
            : pick === "pulse" ? <PulseFilm name={nameOf(pick)} />
            : film ? <RoleFilm key={film.id} film={film} name={nameOf(pick)} covers={covers} people={people} /> : <WhyStory />}
        </div>
        <p className="text-sm text-muted-foreground">{tStory("hint")}</p>
      </div>

      <section aria-labelledby="role-films-title" className="space-y-3">
        <div className="space-y-1">
          <h2 id="role-films-title" className="text-xl font-bold">{t("title")}</h2>
          <p className="text-sm text-muted-foreground">{t("intro")}</p>
        </div>
        <ul className="grid grid-cols-[repeat(2,minmax(0,1fr))] gap-2 sm:grid-cols-[repeat(4,minmax(0,1fr))] lg:grid-cols-[repeat(5,minmax(0,1fr))] xl:grid-cols-[repeat(8,minmax(0,1fr))]">
          {order.map((key, i) => {
            const Icon = ICONS[key];
            const on = pick === key;
            const name = nameOf(key);
            return (
              <li key={key}>
                <button
                  type="button"
                  onClick={() => choose(key)}
                  aria-pressed={on}
                  aria-label={t("play", { name })}
                  className={cn(
                    "group relative block aspect-square w-full overflow-hidden rounded-[var(--thumbnail-radius)] bg-[#171512] text-left text-[#f5f1ea] ring-offset-2 ring-offset-background transition-shadow",
                    on ? "ring-2 ring-foreground" : "hover:ring-2 hover:ring-foreground/50",
                  )}
                >
                  {covers[i] && (
                    <Image src={covers[i]} alt="" fill sizes="(min-width: 1280px) 12vw, (min-width: 640px) 25vw, 50vw" className="object-cover opacity-50 transition-opacity group-hover:opacity-70" />
                  )}
                  <span aria-hidden className="absolute inset-0 bg-gradient-to-t from-[#171512] via-[#171512]/45 to-transparent" />
                  {mine.includes(key as RoleKey) && (
                    <span className="absolute left-2 top-2 rounded-full bg-[#f5f1ea] px-2 py-0.5 text-2xs font-bold text-[#171512]">{t("yours")}</span>
                  )}
                  {on && (
                    <span aria-hidden className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-[#f5f1ea] text-[#171512]">
                      <Play className="h-3.5 w-3.5 fill-current" />
                    </span>
                  )}
                  <span className="absolute inset-x-3 bottom-3 flex flex-col gap-1.5">
                    <Icon className="h-6 w-6" aria-hidden />
                    <span className="text-sm font-bold leading-tight">{name}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </section>
    </div>
  );
}
